import { Button } from "antd";
import { Card } from "antd";
import { useLoginWithLinkedin } from "Features/Auth/hooks/useLoginWithLinkedin";
import LoginModal from "Features/Navbar/LoginModal";
import {
  Briefcase,
  GraduationCap,
  Users,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { useState } from "react";

const roles = [
  {
    id: "hero",
    title: "OG Hero",
    icon: Briefcase,
    emoji: "💼",
    description: "Working professionals/entrepreneurs guiding future students.",
    keyPoints: [
      "Set price & availability",
      "Share industry insights",
      "Build reputation",
    ],
    ctaText: "Register as Hero",
    color: "primary",
  },
  {
    id: "senior",
    title: "OG Student Counselor",
    icon: GraduationCap,
    emoji: "🎓",
    description:
      "Current college students sharing fresh campus and program insights.",
    keyPoints: ["Peer guidance", "Application tips", "Campus life"],
    ctaText: "Register as Student Counselor",
    color: "secondary",
  },
  {
    id: "counselor",
    title: "OG Counselor",
    icon: Users,
    emoji: "🧑‍🏫",
    description: "Experts in admissions, immigration, finance, or careers.",
    keyPoints: ["Pro consultations", "Specialized advice", "Repeat clientele"],
    ctaText: "Register as Counselor",
    color: "accent",
  },
  {
    id: "representative",
    title: "OG College Representative",
    icon: Users,
    emoji: "🏫",
    description: "Professionals from colleges and universities representing them.",
    keyPoints: [
      "Engage with applicants",
      "Build relationships",
      "Learn college culture",
    ],
    ctaText: "Register as Representative",
  },
];

export function RoleCardsSection() {
  const [loginModal, setLoginModal] = useState(false);
  const login = useLoginWithLinkedin();
  return (
    <section className="py-10 md:py-20 bg-muted/30">
      <div className="max-w-[90%] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
            Choose your role
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {roles.map((role) => {
              return (
                <Card
                  key={role.id}
                  className="group relative overflow-hidden border-2 hover:border-[#80E280]/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl bg-card/80 backdrop-blur-sm"
                >
                  <div className="text-center space-y-4 my-4">
                    <div className="mx-auto w-16 h-16 bg-[#80E280]/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <span className="text-2xl">{role.emoji}</span>
                    </div>

                    <div className="space-y-2">
                      <div className="text-xl font-bold text-card-foreground">
                        {role.title}
                      </div>
                      <div className="text-muted-foreground text-base">
                        {role.description}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-3">
                      {role.keyPoints.map((point, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2"
                        >
                          <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-card-foreground text-base">
                            {point}
                          </span>
                        </div>
                      ))}
                    </div>

                    <Button
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground group-hover:scale-105 transition-transform"
                      size="large"
                      onClick={() => {
                        setLoginModal(true);
                      }}
                    >
                      {role.ctaText}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          <p className="text-base text-muted-foreground bg-muted/50 p-4 rounded-lg max-w-md mx-auto">
            <CheckCircle className="inline h-4 w-4 text-primary mr-2" />
            All mentors are identity-verified by OneGrad.
          </p>
          <LoginModal
            open={loginModal}
            onClose={() => {
              setLoginModal(false);
            }}
            onGoogleLogin={() => {
              setLoginModal(false);
            }}
            onLinkedInLogin={() => {
              login();
            }}
          />
        </div>
      </div>
    </section>
  );
}
